import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { ColumnsWitdhType } from '../../../../types/types';
import { ReloadLocateScrollTarget } from '../../../../types/context';
import { getSumOfColumnWidths } from '../utils/getSumOfColumnWidths';
import { scrollGridToRowSmooth } from '../../../../utils/scrollGridToRowSmooth';

interface UseGridScrollBehaviorProps {
  gridRef: RefObject<HTMLDivElement | any>;
  enabledVirtualScroller: boolean;
  columnsWidth: ColumnsWitdhType;
  expandedRow: any;
  // Fallback scroll target when no expandedRow — first checkbox-selected row
  // is brought into view, on mount/transition only (e.g. returning from
  // detail with selections). Live data changes never scroll to the selection.
  selectedRows?: any[];
  data: any;
  dataKey: string;
  loadMoreRows: () => void;
  // True = near-bottom trigger does not fire, so no lazy fetch can race a
  // locate/search window fetch (dropped response would wedge lazy loading).
  disabledLazyLoading?: boolean;
  onHorizontalScroll?: (value: number) => void;
  // One-shot scroll target armed by the reload locate flow. Consumed and
  // cleared on the next data change; unset = behavior unchanged.
  scrollTargetOverrideRef?: RefObject<ReloadLocateScrollTarget | null>;
}

export const useGridScrollBehavior = ({
  gridRef,
  enabledVirtualScroller,
  columnsWidth,
  expandedRow,
  selectedRows,
  data,
  dataKey,
  loadMoreRows,
  disabledLazyLoading = false,
  onHorizontalScroll,
  scrollTargetOverrideRef,
}: UseGridScrollBehaviorProps) => {
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(true);
  const [hasVerticalOverflow, setHasVerticalOverflow] = useState(true);

  // Tracks which expandedRow[dataKey] we already scrolled to — prevents
  // re-scroll when lazy loading appends data and this effect re-fires.
  const expandedRowScrolledRef = useRef<any>(null);

  // Previous expandedRow / data so the scroll effect can tell whether it
  // re-fired from a target change (scroll) or a lazy-load append (don't yank
  // an already-loaded row back into view).
  const prevExpandedRowRef = useRef<any>(null);
  const prevDataRef = useRef<any>(null);

  // Set by markUserToggle to suppress the next scroll-to-expanded effect
  // (the user already sees the row they clicked — yanking the scroll is jarring).
  // External expandedRow changes (form return, deep link) leave it false and scroll.
  const skipNextScrollRef = useRef(false);

  // Stable ref so the assigned onscroll always calls the latest loadMoreRows /
  // onHorizontalScroll without needing to re-attach the handler to the DOM node.
  const scrollHandlerRef = useRef<((e: any) => void) | null>(null);

  // Last emitted horizontal scroll position; skips redundant context updates on
  // pure vertical scrolls.
  const prevScrollLeftRef = useRef<number>(0);

  const tableContainer = enabledVirtualScroller
    ? '.p-virtualscroller'
    : '.p-datatable-wrapper';

  const sumOfColumnWidths = useMemo(
    () => getSumOfColumnWidths(columnsWidth),
    [columnsWidth]
  );

  // Detects horizontal overflow so Grid can enable the horizontal scrollbar.
  // ResizeObserver fires on column width change or window resize.
  // sumOfColumnWidths is also checked because calculated widths can exceed
  // the measured scrollWidth before the DOM updates (Windows resize bug).
  useEffect(() => {
    if (!gridRef.current) return;
    const observer = new ResizeObserver(() => {
      const el = gridRef.current?.querySelector(tableContainer);
      if (!el || !columnsWidth) return;
      const overflow =
        el.scrollWidth > el.clientWidth || sumOfColumnWidths > el.clientWidth;
      setHasHorizontalOverflow(overflow);
    });
    observer.observe(gridRef.current.querySelector(tableContainer));
    return () => observer.disconnect();
  }, [enabledVirtualScroller, sumOfColumnWidths]);

  // Call from row-click handler to suppress the next scroll-to-expanded.
  // Effect still records the row in expandedRowScrolledRef so it does not
  // retroactively scroll once data lazy-loads.
  const markUserToggle = () => {
    skipNextScrollRef.current = true;
  };

  // Scrolls to the target row once. data is a dep so deep-linked rows scroll
  // in when their page lazy-loads. Suppressed for user row clicks (markUserToggle)
  // and for lazy appends that re-fire with an already-loaded selected row.
  useEffect(() => {
    // A full data replace (reload/filter/sort) swaps in fresh row objects, so
    // the head row reference changes; a lazy append keeps it. On replace, clear
    // the scrolled marker so the expanded row is brought back into view after a
    // reload resets the scroll position.
    const prevData = prevDataRef.current;
    const isLazyAppend =
      !!prevData &&
      !!data &&
      data.length > prevData.length &&
      prevData[0] === data[0];
    if (!isLazyAppend) {
      expandedRowScrolledRef.current = null;
    }
    prevDataRef.current = data;

    // Pick scroll target: expandedRow first, else first checkbox-selected row.
    const targetRow = expandedRow ?? selectedRows?.[0];
    const expandedRowChanged = expandedRow !== prevExpandedRowRef.current;
    prevExpandedRowRef.current = expandedRow;
    prevDataRef.current = data;

    // One-shot override: scroll to the armed row and clear it (even when the
    // row is missing — a stale target must not affect a later scroll). onDone
    // always fires so the arming flow can release its loading state.
    const overrideTarget = scrollTargetOverrideRef?.current ?? null;
    if (scrollTargetOverrideRef && overrideTarget !== null) {
      scrollTargetOverrideRef.current = null;
      const overrideIndex = data?.findIndex(
        (row: any) => String(row?.[dataKey]) === String(overrideTarget.rowId)
      );
      if (overrideIndex >= 0) {
        skipNextScrollRef.current = false;
        scrollGridToRowSmooth(gridRef, overrideIndex, overrideTarget.onDone);
        return;
      }
      overrideTarget.onDone?.();
    }

    if (!targetRow) {
      expandedRowScrolledRef.current = null;
      skipNextScrollRef.current = false;
      return;
    }
    // The selected-rows fallback serves mount/transition only (returning from
    // detail with a selection). On a live grid, a data change (sort/filter
    // replace, locate window fetch, lazy append) must not yank the viewport
    // to the selection — explicit scrolls go through the override ref above.
    // No marker write: a later expandedRow scroll to the same row must still run.
    if (!expandedRow && prevData && prevData.length > 0) {
      return;
    }
    if (expandedRowScrolledRef.current === targetRow[dataKey]) return;

    const index = data?.findIndex(
      (row: any) => row[dataKey] === targetRow[dataKey]
    );
    if (index >= 0) {
      expandedRowScrolledRef.current = targetRow[dataKey];
      if (skipNextScrollRef.current) {
        skipNextScrollRef.current = false;
        return;
      }
      // Lazy load appends rows (old rows stay as a prefix, array grows);
      // sort/filter/refresh replace the array instead. Suppress the scroll
      // only on an append whose target was already loaded.
      const isLazyAppend =
        Array.isArray(prevData) &&
        Array.isArray(data) &&
        prevData.length > 0 &&
        data.length > prevData.length &&
        data[prevData.length - 1]?.[dataKey] ===
          prevData[prevData.length - 1]?.[dataKey];
      const targetAlreadyLoaded = prevData?.some(
        (row: any) => row[dataKey] === targetRow[dataKey]
      );
      if (!expandedRowChanged && isLazyAppend && targetAlreadyLoaded) return;
      if (index > 0) {
        scrollGridToRowSmooth(gridRef, index, undefined, !!expandedRow);
      }
    }
  }, [expandedRow, data]);

  // Keep scrollHandlerRef current so the onscroll closure is never stale
  // even though the handler is attached to the DOM only once on mount.
  useEffect(() => {
    scrollHandlerRef.current = (e: any) => {
      const scrollLeft = e.currentTarget.scrollLeft;
      if (scrollLeft !== prevScrollLeftRef.current) {
        prevScrollLeftRef.current = scrollLeft;
        onHorizontalScroll?.(scrollLeft);
      }
      // Trigger lazy load when user scrolls within 650px of the bottom
      if (
        !disabledLazyLoading &&
        e.currentTarget.scrollTop + 650 >=
          e.currentTarget.scrollHeight - e.currentTarget.clientHeight
      ) {
        loadMoreRows();
      }
    };
  }, [loadMoreRows, onHorizontalScroll, disabledLazyLoading]);

  // PrimeReact virtualScroller exposes no onScroll prop, so we assign
  // directly on the DOM node after mount.
  useEffect(() => {
    if (!gridRef.current || !enabledVirtualScroller) return;
    const el = gridRef.current.querySelector('.p-virtualscroller');
    el.onscroll = (e: any) => scrollHandlerRef.current?.(e);
  }, [gridRef, enabledVirtualScroller]);

  // Detects vertical overflow to conditionally apply the scrolling CSS class.
  // Needs a short delay with virtualScroller because PrimeReact computes the
  // DOM height asynchronously after rows render.
  useEffect(() => {
    const measure = () => {
      const el = gridRef.current?.querySelector(tableContainer);
      if (el) setHasVerticalOverflow(el.scrollHeight > el.clientHeight);
    };

    if (enabledVirtualScroller) {
      const timeoutId = setTimeout(measure, 10);
      return () => clearTimeout(timeoutId);
    }

    measure();
  }, [data]);

  return { hasHorizontalOverflow, hasVerticalOverflow, markUserToggle };
};
